// COO Reasoning — per-steward verdict list for the Supply Chain Chronology page.
// One row per COO-contributing source: index · label + status dot · verdict chip
// · detail. Collapsed by default. TSX port of the mockup's COOReasoning.
import React from 'react';
import { CooStewardVerdict } from '../../types/products';
import { C, MONO } from './chronologyTokens';

const AMBIGUOUS_VERDICTS = ['ambiguous', 'no country', 'no mfg loc', '—'];

// US detection: backend emits Title-Case country names ("United States"), but
// some sources phrase it "US (DUNS)". Match both so US dissent renders blue.
function isUsVerdict(verdict: string): boolean {
  return verdict.startsWith('US') || verdict.startsWith('United States') || verdict.startsWith('USA');
}

export default function COOReasoning({ stewards, cooCountry }: { stewards?: CooStewardVerdict[]; cooCountry?: string | null }) {
  const [open, setOpen] = React.useState(false);
  const list = stewards || [];
  if (list.length === 0) return null;

  const coo = (cooCountry || '').trim();
  const isCountryVerdict = (v: string) => !!coo && v.startsWith(coo);
  const agree = list.filter(s => isCountryVerdict(s.verdict)).length;

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={{ all: 'unset', cursor: 'pointer', width: '100%', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: open ? C.soft : C.bg, borderBottom: open ? `1px solid ${C.line}` : 'none', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: C.ink, letterSpacing: '0.14em' }}>COO REASONING</span>
          <span style={{ width: 4, height: 4, background: C.border, borderRadius: 999 }} />
          <span style={{ fontSize: 12, color: C.mute }}>
            {list.length} source{list.length !== 1 ? 's' : ''}
            {coo ? ` · ${agree} agree on ${coo}` : ''}
          </span>
        </div>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.mute, letterSpacing: '0.08em' }}>{open ? 'HIDE' : 'SHOW'} ▾</span>
      </button>

      {open && (
        <div>
          {list.map((s, i) => {
            const isCountry = isCountryVerdict(s.verdict);
            const isAmbiguous = AMBIGUOUS_VERDICTS.includes(s.verdict.toLowerCase());
            const isUS = isUsVerdict(s.verdict);
            const dot = isCountry ? C.green : isUS ? C.blue : isAmbiguous ? C.goldAccent : C.mute;
            const verdictBg = isCountry ? C.greenBg : isUS ? C.blueBg : isAmbiguous ? C.goldBg : C.cream;
            const verdictColor = isCountry ? C.green : isUS ? C.blue : isAmbiguous ? C.gold : C.mute;
            return (
              <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '28px 180px 130px 1fr', gap: 12, alignItems: 'center', padding: '10px 18px', borderBottom: i < list.length - 1 ? `1px solid #EFE9DD` : 'none', background: i % 2 ? C.soft : C.bg }}>
                <span style={{ fontFamily: MONO, fontSize: 10, color: C.dim }}>{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.ink }}>{s.label}</div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 2, fontFamily: MONO, fontSize: 9, color: C.dim, letterSpacing: '0.06em' }}>
                    <span style={{ width: 6, height: 6, background: dot, borderRadius: 999 }} />
                    {(s.status || '').toUpperCase()}
                  </div>
                </div>
                <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: verdictBg, color: verdictColor, letterSpacing: '0.06em', justifySelf: 'start', whiteSpace: 'nowrap' }}>{(s.verdict || '').toUpperCase()}</span>
                <span style={{ fontSize: 12, color: C.mute, lineHeight: 1.4 }}>{s.detail}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
